// =======================
// infoPerfilEscola.js
// =======================

import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ModalAtos from "./componente/ModalAtos";
import { api } from "@api/api";

const normalizarTexto = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function ModalEscola({ open, onClose, escola, onOpenAtos }) {
  const modalidades = Array.isArray(escola?.modalidades)
    ? escola.modalidades
    : escola?.modalidade
      ? [escola.modalidade]
      : [];

  const mantenedoras = Array.isArray(escola?.mantenedoras)
    ? escola.mantenedoras
    : [];

  const formatarResponsabilidades = (mantenedora) => {
    const responsabilidades = Array.isArray(mantenedora?.responsabilidades)
      ? mantenedora.responsabilidades
      : Array.isArray(mantenedora?.etapasModalidades)
        ? mantenedora.etapasModalidades
        : [];

    return responsabilidades
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        return item?.nome || item?.etapa || item?.modalidade || "";
      })
      .filter(Boolean)
      .join(", ");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-detalhes-escola"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        style={{
          width: "100%",
          maxWidth: 620,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            padding: { xs: 2, sm: 2.5 },
            outline: "none",
            border: "1px solid #9fcdff",
            borderRadius: 2,
            backgroundColor: "#ffffff",
            boxShadow: "0px 14px 40px rgba(25, 70, 120, 0.18)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                color: "#087cf0",
                backgroundColor: "#eaf4ff",
              }}
            >
              <SchoolRoundedIcon />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography
                id="modal-detalhes-escola"
                variant="h6"
                sx={{
                  color: "#0767d8",
                  fontWeight: 700,
                }}
              >
                {escola?.nome}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#5f6f94",
                }}
              >
                Dados institucionais e vínculos
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              aria-label="Fechar detalhes da escola"
              sx={{
                color: "#3f5184",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              marginTop: 2,
              marginBottom: 1.5,
              borderColor: "#c9def4",
            }}
          />

          <Typography
            sx={{
              color: "#0767d8",
              fontWeight: 700,
              marginBottom: 1.5,
            }}
          >
            Dados da Escola
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                Endereço
              </Typography>

              <Typography variant="body2" sx={{ color: "#31415f" }}>
                {escola?.rua}, {escola?.numero} – {escola?.bairro}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                Município
              </Typography>

              <Typography variant="body2" sx={{ color: "#31415f" }}>
                {escola?.municipio}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                CEP
              </Typography>

              <Typography variant="body2" sx={{ color: "#31415f" }}>
                {escola?.cep}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                Telefone
              </Typography>

              <Typography variant="body2" sx={{ color: "#31415f" }}>
                {escola?.contato}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                E-mail
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#31415f",
                  wordBreak: "break-word",
                }}
              >
                {escola?.email}
              </Typography>
            </Box>
          </Box>

          <Divider
            sx={{
              marginY: 2,
              borderColor: "#c9def4",
            }}
          />

          <Typography
            sx={{
              color: "#0767d8",
              fontWeight: 700,
            }}
          >
            Etapas e modalidades ofertadas
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              marginTop: 1.25,
            }}
          >
            {modalidades.map((modalidade) => (
              <Chip
                key={modalidade}
                label={modalidade}
                size="small"
                variant="outlined"
                sx={{
                  color: "#0767d8",
                  borderColor: "#087cf0",
                  backgroundColor: "#ffffff",
                }}
              />
            ))}
          </Box>

          <Divider
            sx={{
              marginY: 2,
              borderColor: "#c9def4",
            }}
          />

          <Typography
            sx={{
              color: "#0767d8",
              fontWeight: 700,
            }}
          >
            Mantenedoras responsáveis
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 1,
              marginTop: 1.25,
            }}
          >
            {mantenedoras.map((mantenedora) => {
              const responsabilidades = formatarResponsabilidades(mantenedora);

              return (
                <Paper
                  key={mantenedora.id}
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    padding: 1.25,
                    borderColor: "#c9def4",
                    borderRadius: 1.5,
                    boxShadow: "none",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      color: "#087cf0",
                      backgroundColor: "#eaf4ff",
                    }}
                  >
                    <AccountBalanceRoundedIcon fontSize="small" />
                  </Avatar>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#31415f",
                        fontWeight: 600,
                      }}
                    >
                      {mantenedora.nome}
                    </Typography>

                    {responsabilidades && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#5f6f94",
                        }}
                      >
                        Responsável por: {responsabilidades}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })}

            {mantenedoras.length === 0 && (
              <Typography variant="body2" sx={{ color: "#5f6f94" }}>
                Nenhuma mantenedora vinculada.
              </Typography>
            )}
          </Box>

          <Divider
            sx={{
              marginTop: 2,
              marginBottom: 1.5,
              borderColor: "#c9def4",
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={onOpenAtos}
              sx={{
                minWidth: 125,
                textTransform: "none",
                backgroundColor: "#087cf0",
                boxShadow: "none",
              }}
            >
              Ver atos
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </Modal>
  );
}

export default function ListaEscolas() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAtosOpen, setModalAtosOpen] = useState(false);

  const [selectedEscola, setSelectedEscola] = useState(null);
  const [escolas, setEscolas] = useState([]);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(api.listUserSchools(), {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && Array.isArray(data.escolas)) {
          const agrupadas = Object.values(
            data.escolas.reduce((acc, escola) => {
              const chave = escola.id_procura_atos || escola.id;

              const modalidadesRecebidas = Array.isArray(escola.modalidades)
                ? escola.modalidades
                : escola.modalidade
                  ? [escola.modalidade]
                  : [];

              const mantenedorasRecebidas = Array.isArray(escola.mantenedoras)
                ? escola.mantenedoras
                : escola.mantenedora_id
                  ? [
                      {
                        id: escola.mantenedora_id,
                        nome: escola.nome_mantenedora,
                      },
                    ]
                  : [];

              if (!acc[chave]) {
                acc[chave] = {
                  ...escola,
                  modalidades: [...modalidadesRecebidas],
                  mantenedoras: [...mantenedorasRecebidas],
                };

                return acc;
              }

              modalidadesRecebidas.forEach((modalidade) => {
                if (!acc[chave].modalidades.includes(modalidade)) {
                  acc[chave].modalidades.push(modalidade);
                }
              });

              mantenedorasRecebidas.forEach((mantenedora) => {
                const jaExiste = acc[chave].mantenedoras.some(
                  (item) => String(item.id) === String(mantenedora.id),
                );

                if (!jaExiste) {
                  acc[chave].mantenedoras.push(mantenedora);
                }
              });

              return acc;
            }, {}),
          );

          setEscolas(agrupadas);
        } else {
          console.error("Formato inesperado da resposta:", data);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar escolas:", error);
      });
  }, []);

  const handleOpenModal = (escola) => {
    setSelectedEscola(escola);
    setModalOpen(true);
  };

  const handleOpenAtos = (escola) => {
    setSelectedEscola(escola);
    setModalAtosOpen(true);
  };

  const escolasFiltradas = escolas.filter((escola) => {
    const termo = normalizarTexto(busca);

    return (
      normalizarTexto(escola.nome).includes(termo) ||
      normalizarTexto(escola.municipio).includes(termo) ||
      escola.modalidades.some((modalidade) =>
        normalizarTexto(modalidade).includes(termo),
      )
    );
  });

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          padding: { xs: 2, md: 3 },
          border: "1px solid #9fcdff",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#0767d8",
                fontWeight: 700,
              }}
            >
              Escolas vinculadas
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#5f6f94",
                marginTop: 0.5,
              }}
            >
              {/*Uma escola pode possuir mais de uma mantenedora responsável. */}
            </Typography>
          </Box>

          <TextField
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="Pesquisar escola..."
            size="small"
            sx={{
              width: { xs: "100%", md: 325 },

              "& .MuiOutlinedInput-root": {
                minHeight: 48,
                borderRadius: 2,

                "& fieldset": {
                  borderColor: "#9fcdff",
                },

                "&:hover fieldset": {
                  borderColor: "#087cf0",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#087cf0",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "#5f6f94" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            marginTop: 3,
          }}
        >
          {escolasFiltradas.map((escola) => {
            const quantidadeMantenedoras = escola.mantenedoras.length;

            return (
              <Paper
                key={escola.id_procura_atos || escola.id}
                variant="outlined"
                sx={{
                  padding: 2.5,
                  border: "1px solid #9fcdff",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", md: "center" },
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      color: "#087cf0",
                      backgroundColor: "#eaf4ff",
                    }}
                  >
                    <SchoolRoundedIcon fontSize="large" />
                  </Avatar>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: { xs: "none", md: "block" },
                      borderColor: "#c9def4",
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#0767d8",
                        fontWeight: 700,
                      }}
                    >
                      {escola.nome}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        color: "#4f5f85",
                        marginTop: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <LocationOnOutlinedIcon fontSize="small" />

                        <Typography variant="body2">
                          {escola.municipio}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <GroupsOutlinedIcon fontSize="small" />

                        <Typography variant="body2">
                          {quantidadeMantenedoras}{" "}
                          {quantidadeMantenedoras === 1
                            ? "mantenedora responsável"
                            : "mantenedoras responsáveis"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        marginTop: 1.5,
                      }}
                    >
                      {escola.modalidades.map((modalidade) => (
                        <Chip
                          key={modalidade}
                          label={modalidade}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: "#0767d8",
                            borderColor: "#087cf0",
                            backgroundColor: "#ffffff",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenModal(escola)}
                      sx={{
                        minWidth: 142,
                        textTransform: "none",
                        borderColor: "#087cf0",
                        color: "#0767d8",
                      }}
                    >
                      Ver detalhes
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<DescriptionOutlinedIcon />}
                      onClick={() => handleOpenAtos(escola)}
                      sx={{
                        minWidth: 142,
                        textTransform: "none",
                        backgroundColor: "#087cf0",
                        boxShadow: "none",
                      }}
                    >
                      Ver atos
                    </Button>
                  </Box>
                </Box>
              </Paper>
            );
          })}

          {escolasFiltradas.length === 0 && (
            <Typography
              sx={{
                color: "#5f6f94",
                textAlign: "center",
                padding: 3,
              }}
            >
              Nenhuma escola encontrada.
            </Typography>
          )}
        </Box>
      </Paper>

      <ModalEscola
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        escola={selectedEscola}
        onOpenAtos={() => {
          setModalOpen(false);
          setModalAtosOpen(true);
        }}
      />

      <ModalAtos
        open={modalAtosOpen}
        onClose={() => setModalAtosOpen(false)}
        escola={selectedEscola}
      />
    </>
  );
}
